import React, { useEffect, useRef } from 'react';
import { AppSection } from '../types';

// Define props to match App.tsx usage
interface TwoEngineProps {
  onNavigate: (section: AppSection) => void;
}

const TwoEngine: React.FC<TwoEngineProps> = ({ onNavigate }) => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          // Once revealed, we can stop observing this specific element
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 md:mb-24 reveal">
          <div className="mono text-[10px] text-[#0091da] font-bold uppercase tracking-[0.3em] mb-4">// Structural Framework</div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">The Two-Engine Model</h2>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto font-light leading-relaxed">
            Our delivery methodology addresses organizational needs through two specialized, precision-engineered engines.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          <div 
            className="reveal p-8 md:p-12 rounded-sm border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-2xl hover:shadow-blue-900/5 transition-all group border-b-4 border-b-[#0091da]" 
            style={{ transitionDelay: '150ms' }}
          >
            <div className="w-16 h-16 bg-[#0091da]/10 text-[#0091da] rounded-sm flex items-center justify-center mb-10 group-hover:bg-[#0091da] group-hover:text-white transition-all">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            </div>
            <h3 className="text-2xl font-bold text-slate-950 mb-4 tracking-tight">Project Solutions</h3>
            <p className="text-slate-600 leading-relaxed mb-8 font-light text-base">
              End-to-end strategic consulting. We take ownership of the entire outcome, from initial discovery and market analysis to final deployment.
            </p>
            <ul className="space-y-4 text-sm text-slate-500 mb-12">
              <li className="flex items-center"><span className="w-1.5 h-1.5 bg-[#0091da] rounded-full mr-3"></span> Outcome-focused engagement</li>
              <li className="flex items-center"><span className="w-1.5 h-1.5 bg-[#0091da] rounded-full mr-3"></span> Strategic roadmapping & discovery</li>
              <li className="flex items-center"><span className="w-1.5 h-1.5 bg-[#0091da] rounded-full mr-3"></span> Full lifecycle architectural management</li>
            </ul>
            <button 
              onClick={() => onNavigate(AppSection.Solutions)}
              className="text-[#0091da] text-xs font-bold uppercase tracking-widest flex items-center group-hover:translate-x-2 transition-transform"
            >
              Learn More <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          <div 
            className="reveal p-8 md:p-12 rounded-sm border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-2xl hover:shadow-yellow-900/5 transition-all group border-b-4 border-b-[#facc15]" 
            style={{ transitionDelay: '300ms' }}
          >
            <div className="w-16 h-16 bg-[#facc15]/10 text-[#facc15] rounded-sm flex items-center justify-center mb-10 group-hover:bg-[#facc15] group-hover:text-slate-900 transition-all">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            </div>
            <h3 className="text-2xl font-bold text-slate-950 mb-4 tracking-tight">Expert Staffing</h3>
            <p className="text-slate-600 leading-relaxed mb-8 font-light text-base">
              Precision-vetted talent augmentation. Get the right experts—Architects and DevOps—embedded directly into your existing teams.
            </p>
            <ul className="space-y-4 text-sm text-slate-500 mb-12">
              <li className="flex items-center"><span className="w-1.5 h-1.5 bg-[#facc15] rounded-full mr-3"></span> Rapid resource scaling in 48 hours</li>
              <li className="flex items-center"><span className="w-1.5 h-1.5 bg-[#facc15] rounded-full mr-3"></span> Top 2% globally vetted technical talent</li>
              <li className="flex items-center"><span className="w-1.5 h-1.5 bg-[#facc15] rounded-full mr-3"></span> Domain-specific Midwest expertise</li>
            </ul>
            <button 
              onClick={() => onNavigate(AppSection.Talent)}
              className="text-[#facc15] text-xs font-bold uppercase tracking-widest flex items-center group-hover:translate-x-2 transition-transform"
            >
              Find Talent <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TwoEngine;